import { useTranslation } from 'next-i18next';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import { Icon, IconButton, Spinner } from 'components';
import Modal from 'components/Modal';

import { useNotification } from 'hooks/useNotification';
import { useCandidateProfile } from 'lib/contexts/candidate-profile-context';
import { useAuth } from 'lib/providers/AuthProvider';

import { convertToBase64 } from 'utils/format';
import styles from './FloatingCVModal.module.scss';

type FloatingCVModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const FloatingCVModal: React.FC<FloatingCVModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { candidate } = useAuth();
  const [resumeUrl, setResumeUrl] = useState<string | null>(null);
  const [resumeName, setResumeName] = useState<string | null>(null);
  const { generateCV, updateCandidate } = useCandidateProfile();
  const [t] = useTranslation('candidate.profile');
  const notificationInstance = useNotification();

  const [isResumeUploading, setIsResumeUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (candidate) {
      setResumeUrl(candidate.resume_url);
    }
  }, [candidate]);

  useEffect(() => {
    if (candidate) {
      setResumeName(candidate.resume.filename);
    }
  }, [candidate]);

  const onDownload = () => {
    if (resumeUrl) saveAs(resumeUrl);
  };

  const onRemove = async () => {
    await updateCandidate({ resume: { _destroy: true } }, false, true);
    onClose();
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setIsResumeUploading(true);
      if (file.size > 2097152) {
        setIsResumeUploading(false);

        notificationInstance.error({
          title: t('floating-cv-modal.file-size-error.title'),
          message: t('floating-cv-modal.file-size-error.message'),
        });
        return;
      }
      const fileInBase64String = await convertToBase64(file);

      await updateCandidate({
        resume: {
          name: file.name,
          file: fileInBase64String,
        },
      });
      onClose();
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setIsResumeUploading(false);
  };

  const handleResumeUpload = () => {
    fileInputRef.current?.click();
  };

  const downloadCV = async () => {
    setIsDownloading(true);
    await generateCV();
    setIsDownloading(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={styles['modal-container']}
      contentClassName={styles['modal-content']}
    >
      <article className={styles.content}>
        {resumeUrl ? (
          <section className={styles['upper-box']}>
            <div className={styles.row}>
              <p className={styles.text}>
                {t('floating-cv-modal.remove', { resume: resumeName })}
              </p>
              <div onClick={onRemove}>
                <Icon iconName="icon_trash" className={styles.trash} />
              </div>
            </div>
            <div className={styles.row}>
              <p className={styles.text}>{t('floating-cv-modal.replace')}</p>
              <div>
                {isResumeUploading ? (
                  <Spinner size="small" />
                ) : (
                  <IconButton
                    iconName="icon_arrow-up-2"
                    className={styles.replace}
                    onClick={handleResumeUpload}
                  />
                )}
              </div>
            </div>
            <div className={styles.row}>
              <p className={styles.text}>
                {t('floating-cv-modal.download', { resume: resumeName })}
              </p>
              <div onClick={onDownload}>
                <Icon iconName="icon_download" className={styles.download} />
              </div>
            </div>
          </section>
        ) : (
          <section className={styles['upper-box']}>
            <div className={styles.row}>
              <p className={styles.text}>{t('floating-cv-modal.upload')}</p>
              <div>
                {isResumeUploading ? (
                  <Spinner size="small" />
                ) : (
                  <IconButton
                    iconName="icon_attach"
                    className={styles.attach}
                    onClick={handleResumeUpload}
                  />
                )}
              </div>
            </div>
          </section>
        )}
        <section className={styles['lower-box']}>
          <div className={styles['row-down']}>
            <p className={styles.text}>{t('floating-cv-modal.generate')}</p>
            {isDownloading ? (
              <div className={styles['magic-wand']}>
                <Spinner
                  className={cn(styles['magic-wand'], styles.spinner)}
                  color={'secondary'}
                />
              </div>
            ) : (
              <div onClick={downloadCV}>
                <Icon
                  iconName="icon_magic-wand"
                  className={styles['magic-wand']}
                />
              </div>
            )}
          </div>
        </section>
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
      </article>
    </Modal>
  );
};
